import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { FlaskConical } from "lucide-react";

const SEL = "h-8 rounded-md bg-[var(--bg-base,#0b1120)] border border-[var(--border-subtle)] px-2 text-xs text-slate-200";
const COSTS = [["fee_bps", "Fee bps"], ["slippage_bps", "Slippage bps"], ["initial_capital", "Capital"], ["risk_pct", "Risk %/trade"], ["alloc_pct", "Alloc % (no stop)"], ["oos_fraction", "Out-of-sample"]];

function Num({ label, value, onChange, testId }) {
  return (
    <label className="flex flex-col gap-1 text-slate-500">{label}
      <input data-testid={testId} type="number" step="any" className={`${SEL} w-28`} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function BacktestForm({ onResult }) {
  const ds = useQuery({ queryKey: ["hist-datasets"], queryFn: async () => (await api.get("/history/datasets")).data });
  const st = useQuery({ queryKey: ["bt-strategies"], queryFn: async () => (await api.get("/backtest/strategies")).data });
  const [sel, setSel] = useState("");
  const [strategy, setStrategy] = useState("sma_cross_reference");
  const [params, setParams] = useState({});
  const [cost, setCost] = useState({ fee_bps: 10, slippage_bps: 5, initial_capital: 10000, risk_pct: 1, alloc_pct: 100, oos_fraction: 0.3 });
  const items = ds.data?.items || [];
  const dset = items.find((d) => `${d.source}|${d.symbol}|${d.interval}` === sel) || items[0];
  const strat = (st.data?.items || []).find((s) => s.name === strategy);
  const run = useMutation({
    mutationFn: async () => (await api.post("/backtest/run", { source: dset.source, symbol: dset.symbol, interval: dset.interval, strategy,
      params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])),
      ...Object.fromEntries(Object.entries(cost).map(([k, v]) => [k, Number(v)])) })).data,
    onSuccess: (r) => { onResult(r); toast.success(r.reproduced ? "Identical run reproduced (same hash)" : "Backtest complete"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Panel title="Run backtest" testId="backtest-form" actions={<FlaskConical className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-3 text-xs">
        <div className="flex flex-wrap gap-2">
          <select data-testid="bt-dataset" className={SEL} value={sel} onChange={(e) => setSel(e.target.value)}>
            {items.map((d) => <option key={`${d.source}|${d.symbol}|${d.interval}`} value={`${d.source}|${d.symbol}|${d.interval}`}>{d.symbol} {d.interval} ({d.count})</option>)}
          </select>
          <select data-testid="bt-strategy" className={SEL} value={strategy} onChange={(e) => { setStrategy(e.target.value); setParams({}); }}>
            {(st.data?.items || []).map((s) => <option key={s.name} value={s.name}>{s.label}</option>)}
          </select>
        </div>
        {strat && !strat.runnable && <div data-testid="bt-gtf-locked" className="text-amber-300">{strat.reason}</div>}
        <div className="flex flex-wrap gap-3">
          {Object.entries(strat?.params || {}).map(([k, [, , def]]) => (
            <Num key={k} label={k} testId={`bt-param-${k}`} value={params[k] ?? def} onChange={(v) => setParams({ ...params, [k]: v })} />))}
          {COSTS.map(([k, l]) => <Num key={k} label={l} testId={`bt-${k}`} value={cost[k]} onChange={(v) => setCost({ ...cost, [k]: v })} />)}
        </div>
        <Button data-testid="bt-run-btn" size="sm" disabled={!dset || run.isPending || (strat && !strat.runnable)} onClick={() => run.mutate()}>{run.isPending ? "Running…" : "Run backtest"}</Button>
        {ds.isLoading && <div className="text-slate-500">Loading datasets…</div>}
        {!ds.isLoading && !items.length && <div className="text-slate-500">Ingest history first.</div>}
      </div>
    </Panel>
  );
}
